/**
 * LOT-UI-03b.1 — Header global unifié (mobile-first, DS Remobiles).
 */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, Globe, ChevronDown, Wrench, Search, MoreHorizontal, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { SITE } from '../../utils/siteConfig';
import { getStaffHomeRoute, getRoleLabel } from '../../utils/roleRouting';
import MagasinSessionBadge from '../MagasinSessionBadge';
import StaffNotifications from '../StaffNotifications';
import GlobalSearch from './GlobalSearch';
import CaisseHeaderChip from './CaisseHeaderChip';

const AppHeader = ({ onMenuToggle, menuOpen, navCollapsed, onNavCollapseToggle, onOpenSearch, onOpenPlus, posOnly = false }) => {
  const { user, logout, isAdmin, api } = useAuth();
  const { t, lang, setLanguage, languages } = useLanguage();
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const [companyLogo, setCompanyLogo] = useState(null);
  const initial = user?.username ? user.username.charAt(0).toUpperCase() : 'U';
  const homeRoute = getStaffHomeRoute(user);

  useEffect(() => {
    if (!api || !user) return;
    api.get('/company-settings')
      .then((res) => { if (res.data?.logo_url) setCompanyLogo(res.data.logo_url); })
      .catch(() => {});
  }, [api, user]);

  return (
    <header
      className="remobiles-topbar sticky top-0 z-50 h-16 px-2 sm:px-3 md:px-4 flex items-center gap-2 sm:gap-3 backdrop-blur-xl"
      style={{ borderBottom: '1px solid var(--rm-border-subtle)' }}
      data-testid="app-header"
    >
      <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0 min-w-0">
        <button
          type="button"
          onClick={onMenuToggle}
          className="rm-touch rounded-xl transition-colors md:hidden"
          style={{
            border: '1px solid var(--rm-border-subtle)',
            background: 'var(--rm-bg-panel)',
            color: 'var(--rm-gray-100)',
          }}
          aria-label={menuOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
          data-testid="menu-toggle"
        >
          {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
        <button
          type="button"
          onClick={onNavCollapseToggle}
          className="hidden md:flex rm-touch rounded-xl transition-colors"
          style={{
            border: '1px solid var(--rm-border-subtle)',
            background: 'var(--rm-bg-panel)',
            color: 'var(--rm-gray-100)',
          }}
          aria-label={navCollapsed ? 'Afficher le menu latéral' : 'Masquer le menu latéral'}
          data-testid="menu-toggle-desktop"
        >
          {navCollapsed ? <PanelLeftOpen className="w-5 h-5" /> : <PanelLeftClose className="w-5 h-5" />}
        </button>
        <Link to={homeRoute} className="flex items-center gap-2 px-1.5 sm:px-2 py-1 rounded-xl hover:bg-white/5 transition-colors min-w-0">
          <div
            className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center overflow-hidden flex-shrink-0"
            style={{ background: companyLogo ? 'transparent' : 'var(--rm-red-muted)' }}
          >
            {companyLogo ? (
              <img src={`${process.env.REACT_APP_BACKEND_URL}${companyLogo}`} alt={SITE.name} className="w-full h-full object-contain rounded-xl" />
            ) : SITE.isRemobiles ? (
              <Wrench className="w-4 h-4 text-[var(--rm-red-light)]" />
            ) : (
              <img src="/guyarepair_logo.png" alt="GUYAREPAIR" className="w-full h-full object-contain rounded-xl" />
            )}
          </div>
          <div className="hidden sm:block min-w-0">
            <div className="font-bold text-sm leading-tight truncate text-[var(--rm-white)]">{user?.company_name || SITE.name}</div>
            <div className="text-[10px] text-[var(--rm-gray-500)] truncate">{getRoleLabel(user)}</div>
          </div>
        </Link>
      </div>

      {!posOnly && (
        <div className="hidden lg:flex flex-1 max-w-md mx-auto">
          <GlobalSearch api={api} />
        </div>
      )}

      {!posOnly && (
        <button
          type="button"
          onClick={onOpenSearch}
          className="lg:hidden rm-touch flex items-center gap-2 flex-1 max-w-[120px] sm:max-w-[160px] px-3 rounded-xl border text-sm min-h-[44px]"
          style={{
            borderColor: 'var(--rm-border-subtle)',
            background: 'var(--rm-bg-panel)',
            color: 'var(--rm-gray-500)',
          }}
          data-testid="header-search-mobile"
        >
          <Search className="w-4 h-4 flex-shrink-0" />
          <span className="truncate">Rechercher</span>
        </button>
      )}
      {posOnly && <div className="flex-1" />}

      <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0 ml-auto">
        <MagasinSessionBadge />
        {!posOnly && (
          <div className="hidden md:block">
            <CaisseHeaderChip />
          </div>
        )}
        <div className="relative hidden lg:block">
          <button
            type="button"
            onClick={() => setLangMenuOpen(!langMenuOpen)}
            className="rm-touch px-2.5 rounded-xl border text-sm gap-1.5"
            style={{ borderColor: 'var(--rm-border-subtle)', background: 'var(--rm-bg-panel)' }}
            data-testid="lang-selector-btn"
          >
            <Globe className="w-4 h-4 text-[var(--rm-gray-400)]" />
            <span className="text-[var(--rm-gray-300)]">{languages.find((l) => l.code === lang)?.flag}</span>
            <ChevronDown className="w-3 h-3 text-[var(--rm-gray-500)]" />
          </button>
          {langMenuOpen && (
            <>
              <div className="fixed inset-0 z-50" onClick={() => setLangMenuOpen(false)} />
              <div
                className="absolute right-0 top-full mt-1 w-48 max-h-64 overflow-y-auto rounded-xl shadow-2xl z-50"
                style={{ border: '1px solid var(--rm-border-subtle)', background: 'var(--rm-bg-panel)' }}
              >
                {languages.map((l) => (
                  <button
                    key={l.code}
                    type="button"
                    onClick={() => { setLanguage(l.code); setLangMenuOpen(false); }}
                    className={`w-full text-left px-3 py-2.5 min-h-[44px] text-sm hover:bg-white/5 flex items-center gap-2 ${lang === l.code ? 'font-semibold text-[var(--rm-red)]' : 'text-[var(--rm-gray-300)]'}`}
                  >
                    <span>{l.flag}</span><span>{l.name}</span>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
        {!posOnly && <StaffNotifications api={api} isAdmin={isAdmin} />}
        {!posOnly && (
          <button
            type="button"
            onClick={onOpenPlus}
            className="rm-touch rounded-xl border text-xs font-semibold gap-1.5 px-2.5 sm:px-3"
            style={{
              borderColor: 'var(--rm-border-subtle)',
              background: 'var(--rm-bg-panel)',
              color: 'var(--rm-gray-300)',
            }}
            data-testid="header-plus-btn"
          >
            <MoreHorizontal className="w-4 h-4 md:hidden" />
            <span className="hidden md:inline">Plus</span>
          </button>
        )}
        <div
          className="hidden sm:flex items-center gap-2 px-2 py-1 rounded-xl border min-h-[44px]"
          style={{ borderColor: 'var(--rm-border-subtle)', background: 'var(--rm-bg-panel)' }}
        >
          <div
            className="w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs text-white"
            style={{ background: 'var(--rm-red-muted)', border: '1px solid var(--rm-border-strong)' }}
          >
            {initial}
          </div>
          <div className="hidden md:block">
            <div className="font-bold text-xs text-[var(--rm-white)]">{user?.username}</div>
          </div>
        </div>
        <button
          type="button"
          onClick={logout}
          className="hidden lg:inline text-xs text-[var(--rm-red-light)] hover:text-[var(--rm-red)] px-2 min-h-[44px]"
          data-testid="header-logout"
        >
          {t('nav.logout')}
        </button>
      </div>
    </header>
  );
};

export default AppHeader;
